import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
  useTheme,
  alpha,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  TableRestaurant,
  ExpandMore,
  AccessTime,
  Person
} from '@mui/icons-material';
import { TableSummary, formatCurrency, getPaymentStatusInfo } from '../../api/invoiceApi';
import { AppIconButton } from '../ui';
import SessionTimeline from './SessionTimeline';
import { useSessionTimeline } from '../../hooks/useSessionTimeline';
import { DateRange, getDateRangeForPreset } from './DateRangeSelector';

interface TableCardProps {
  table: TableSummary;
  onClick?: (tableId: number) => void;
  expanded?: boolean;
  onExpandClick?: (tableId: number) => void;
  /** Date range for session filtering (required when expandable) */
  dateRange?: DateRange;
  /** Whether this card is expandable to show timeline */
  expandable?: boolean;
}

/**
 * TableCard - Displays table summary with session statistics.
 * Shows active session info and allows expanding to see timeline.
 */
const TableCard: React.FC<TableCardProps> = ({
  table,
  onClick,
  expanded: controlledExpanded,
  onExpandClick,
  dateRange,
  expandable = true
}) => {
  const theme = useTheme();
  const [internalExpanded, setInternalExpanded] = useState(false);

  // Determine if controlled or uncontrolled expansion
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  // Use session timeline hook when expanded
  const {
    sessions,
    loading: loadingSessions,
    error: sessionsError
  } = useSessionTimeline({
    tableId: expanded ? table.id : null,
    dateRange: dateRange || getDateRangeForPreset('today'),
    autoFetch: expanded
  });

  const handleClick = () => {
    onClick?.(table.id);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onExpandClick) {
      onExpandClick(table.id);
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const hasActiveSession = table.hasActiveSession && table.activeSession;

  return (
    <Paper
      elevation={expanded ? 3 : 1}
      sx={{
        borderRadius: 2,
        border: hasActiveSession
          ? `2px solid ${theme.palette.success.main}`
          : `1px solid ${theme.palette.divider}`,
        backgroundColor: expanded
          ? alpha(theme.palette.primary.main, 0.02)
          : theme.palette.background.paper,
        transition: 'all 0.2s ease',
        overflow: 'hidden'
      }}
    >
      {/* Main Card Content */}
      <Box
        onClick={handleClick}
        sx={{
          p: 2,
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            backgroundColor: alpha(theme.palette.action.hover, 0.5)
          } : {}
        }}
      >
        {/* Header Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hasActiveSession
                  ? alpha(theme.palette.success.main, 0.1)
                  : alpha(theme.palette.grey[500], 0.1),
                color: hasActiveSession
                  ? theme.palette.success.main
                  : theme.palette.grey[600]
              }}
            >
              <TableRestaurant sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                Table {table.displayNumber || table.name}
              </Typography>
              {table.tableNumber && table.tableNumber !== parseInt(table.displayNumber || '0') && (
                <Typography variant="caption" color="text.secondary">
                  #{table.tableNumber}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasActiveSession && (
              <Chip
                label="Active"
                size="small"
                color="success"
                sx={{ height: 22 }}
              />
            )}
            {expandable && (
              <AppIconButton
                icon={<ExpandMore />}
                tooltip={expanded ? 'Collapse' : 'Expand'}
                variant="ghost"
                size="small"
                onClick={handleExpandClick}
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              />
            )}
          </Box>
        </Box>

        {/* Stats Row */}
        <Box sx={{ display: 'flex', gap: 2, mb: hasActiveSession ? 1.5 : 0 }}>
          <StatItem
            label="Sessions"
            value={table.sessionCount.toString()}
            color="text.primary"
          />
          <StatItem
            label="Revenue"
            value={formatCurrency(table.totalRevenue)}
            color="success.main"
          />
          {table.sessionCount > 0 && (
            <StatItem
              label="Avg"
              value={formatCurrency(table.averageSessionValue)}
              color="text.secondary"
            />
          )}
        </Box>

        {/* Active Session Info */}
        {hasActiveSession && table.activeSession && (
          <Box
            sx={{
              mt: 1,
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Current Session
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {new Date(table.activeSession.startedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
                {table.activeSession.customer && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {table.activeSession.customer.name}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(table.activeSession.totalAmount)}
                </Typography>
                <Chip
                  label={getPaymentStatusInfo(table.activeSession.paymentStatus).text}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    backgroundColor: alpha(getPaymentStatusInfo(table.activeSession.paymentStatus).color, 0.1),
                    color: getPaymentStatusInfo(table.activeSession.paymentStatus).color
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Expanded Timeline Section */}
      {expandable && (
        <Collapse in={expanded}>
          <Box
            sx={{
              px: 2,
              pb: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.background.default, 0.5)
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, py: 1.5 }}
            >
              Session Timeline
            </Typography>

            {loadingSessions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : sessionsError ? (
              <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 2 }}>
                {sessionsError}
              </Typography>
            ) : (
              <SessionTimeline
                sessions={sessions}
                loading={loadingSessions}
                displayMode="list"
                rangeStart={dateRange?.startDate}
                rangeEnd={dateRange?.endDate}
              />
            )}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

interface StatItemProps {
  label: string;
  value: string;
  color: string;
}

const StatItem: React.FC<StatItemProps> = ({ label, value, color }) => {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color }}>
        {value}
      </Typography>
    </Box>
  );
};

// Skeleton for loading state
export const TableCardSkeleton: React.FC = () => {
  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="rounded" width={36} height={36} />
          <Box>
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={40} height={14} />
          </Box>
        </Box>
        <Skeleton variant="rounded" width={50} height={22} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Skeleton variant="text" width={60} height={32} />
        <Skeleton variant="text" width={80} height={32} />
        <Skeleton variant="text" width={60} height={32} />
      </Box>
    </Paper>
  );
};

export default TableCard;
